'use client';
// Shared components and utilities for the AmanChain interface
import { useEffect, useRef, useState, useCallback } from 'react';
import { Copy, Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

// ---------- Brand & token avatars ----------
export const AMAN_LOGO = '/aman-logo.png';

function symbolHue(sym: string): string {
  let h = 0;
  for (let i = 0; i < sym.length; i++) h = (h * 31 + sym.charCodeAt(i)) % 360;
  return `hsl(${h} 65% 45%)`;
}

/**
 * Token/coin avatar: shows the token's consensus-stored logo when it has one,
 * the AMAN coin brand for the native coin, otherwise a clean symbol tile.
 */
export function TokenAvatar({ symbol, logo, size = 32, className }: {
  symbol: string; logo?: string | null; size?: number; className?: string;
}) {
  const isAman = symbol.toUpperCase() === 'AMAN';
  const src = isAman ? AMAN_LOGO : logo || null;
  const style = { width: size, height: size, fontSize: Math.max(9, Math.round(size * 0.34)) };
  if (src) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={src} alt={symbol} width={size} height={size} style={style}
        className={cn('rounded-full object-cover ring-1 ring-border shrink-0 bg-background', className)}
        onError={e => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
      />
    );
  }
  return (
    <span
      style={{ ...style, color: symbolHue(symbol), background: `color-mix(in srgb, ${symbolHue(symbol)} 12%, transparent)` }}
      className={cn('inline-flex items-center justify-center rounded-lg font-black leading-none ring-1 ring-border shrink-0 tracking-tight', className)}
    >
      {symbol.slice(0, 4)}
    </span>
  );
}

// ---------- Requests ----------
export async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  const data = await res.json().catch(() => ({ error: 'Invalid response from the server' }));
  if (!res.ok || data?.error) throw new Error(data?.error || `Error ${res.status}`);
  return data as T;
}

export function post<T>(url: string, body: unknown): Promise<T> {
  return fetchJson<T>(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// ---------- Polling hook ----------
export function usePoll<T>(fn: () => Promise<T>, ms = 3000, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const fnRef = useRef(fn);
  useEffect(() => { fnRef.current = fn; });

  const refresh = useCallback(() => {
    fnRef.current()
      .then(d => { setData(d); setError(null); })
      .catch(e => setError(e instanceof Error ? e.message : 'Error'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, ms);
    return () => clearInterval(t);
  }, [ms, refresh, ...deps]);

  return { data, error, loading, refresh };
}

// ---------- Formatting ----------
export const fmtAmt = (n: number | undefined | null, max = 4): string =>
  n === undefined || n === null ? '—' : new Intl.NumberFormat('en-US', { maximumFractionDigits: max }).format(n);

export const fmtInt = (n: number | undefined | null): string =>
  n === undefined || n === null ? '—' : new Intl.NumberFormat('en-US').format(n);

export const shortHash = (h: string, head = 10, tail = 8): string =>
  h.length > head + tail + 3 ? `${h.slice(0, head)}…${h.slice(-tail)}` : h;

export const timeAgo = (ts: number): string => {
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (s < 5) return 'now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export const fmtDuration = (ms: number): string => {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
};

export const fmtHashrate = (h: number): string => {
  if (h >= 1e6) return `${(h / 1e6).toFixed(2)} MH/s`;
  if (h >= 1e3) return `${(h / 1e3).toFixed(1)} kH/s`;
  return `${Math.round(h)} H/s`;
};

// ---------- Copy to clipboard ----------
export function CopyBtn({ text, className }: { text: string; className?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      aria-label="Copy"
      onClick={async () => {
        try { await navigator.clipboard.writeText(text); } catch { /* fallback */ }
        setDone(true);
        setTimeout(() => setDone(false), 1200);
      }}
      className={cn('inline-flex items-center justify-center text-muted-foreground hover:text-primary transition-colors', className)}
    >
      {done ? <Check className="size-3.5 text-primary" /> : <Copy className="size-3.5" />}
    </button>
  );
}

export function Mono({ children, className, dir }: { children: React.ReactNode; className?: string; dir?: 'ltr' | 'rtl' }) {
  return <span dir={dir} className={cn('mono text-[0.72rem] text-muted-foreground', className)}>{children}</span>;
}

// ---------- Stat card ----------
export function StatCard({ icon: Icon, label, value, sub, accent = 'emerald' }: {
  icon: React.ComponentType<{ className?: string }>;
  label: string; value: React.ReactNode; sub?: string; accent?: 'emerald' | 'gold' | 'sky' | 'rose' | 'cyan' | 'violet';
}) {
  const accents: Record<string, string> = {
    emerald: 'text-emerald-400 bg-emerald-500/10 ring-emerald-500/20',
    gold: 'text-amber-400 bg-amber-500/10 ring-amber-500/20',
    sky: 'text-sky-400 bg-sky-500/10 ring-sky-500/20',
    rose: 'text-rose-400 bg-rose-500/10 ring-rose-500/20',
    cyan: 'text-cyan-400 bg-cyan-500/10 ring-cyan-500/20',
    violet: 'text-violet-400 bg-violet-500/10 ring-violet-500/20',
  };
  return (
    <div className="glass glass-hover p-4 flex items-start gap-3">
      <div className={cn('rounded-lg p-2.5 ring-1 shrink-0', accents[accent])}>
        <Icon className="size-4.5" />
      </div>
      <div className="min-w-0">
        <div className="text-[0.72rem] text-muted-foreground font-medium">{label}</div>
        <div className="text-lg font-bold leading-tight mt-0.5 truncate" dir="ltr">{value}</div>
        {sub && <div className="text-[0.68rem] text-muted-foreground mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

// ---------- Address chip ----------
export function AddressChip({ address, short = true }: { address: string; short?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1 max-w-full">
      <Mono className="truncate">{short ? shortHash(address, 10, 6) : address}</Mono>
      <CopyBtn text={address} />
    </span>
  );
}

// ---------- Transaction type badge ----------
const TYPE_STYLE: Record<string, string> = {
  transfer: 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/25',
  coinbase: 'bg-amber-500/10 text-amber-400 ring-amber-500/25',
  faucet: 'bg-sky-500/10 text-sky-400 ring-sky-500/25',
  token_create: 'bg-violet-500/10 text-violet-400 ring-violet-500/25',
  token_mint: 'bg-fuchsia-500/10 text-fuchsia-400 ring-fuchsia-500/25',
  token_burn: 'bg-orange-500/10 text-orange-400 ring-orange-500/25',
  token_transfer: 'bg-violet-500/10 text-violet-300 ring-violet-500/25',
  stake: 'bg-teal-500/10 text-teal-400 ring-teal-500/25',
  unstake: 'bg-orange-500/10 text-orange-400 ring-orange-500/25',
  claim_stake: 'bg-teal-500/10 text-teal-300 ring-teal-500/25',
  genesis: 'bg-slate-500/10 text-slate-600 ring-slate-500/25 dark:text-slate-300',
  pool_create: 'bg-cyan-500/10 text-cyan-400 ring-cyan-500/25',
  pool_add: 'bg-sky-500/10 text-sky-300 ring-sky-500/25',
  pool_remove: 'bg-rose-500/10 text-rose-400 ring-rose-500/25',
  swap: 'bg-emerald-500/10 text-emerald-300 ring-emerald-500/25',
  admin_param: 'bg-amber-500/10 text-amber-300 ring-amber-500/25',
  admin_freeze: 'bg-rose-500/10 text-rose-300 ring-rose-500/25',
};

export function TypeBadge({ type, label }: { type: string; label?: string }) {
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[0.65rem] font-semibold ring-1 whitespace-nowrap', TYPE_STYLE[type] ?? 'bg-muted text-muted-foreground ring-border')}>
      {label ?? type}
    </span>
  );
}

// ---------- Quantum shield badge ----------
export function QuantumBadge({ safe = true, compact = false, className }: {
  safe?: boolean; compact?: boolean; className?: string;
}) {
  if (!safe) return null;
  return (
    <span
      title="Signed twice: Ed25519 + ML-DSA-87 (NIST FIPS 204) — protected against quantum computers"
      className={cn(
        'inline-flex items-center gap-1 rounded-full bg-cyan-500/10 text-cyan-300 ring-1 ring-cyan-400/30 px-2 py-0.5 text-[0.62rem] font-bold whitespace-nowrap',
        className,
      )}
    >
      <svg viewBox="0 0 24 24" className="size-3" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
        <circle cx="12" cy="12" r="2.4" />
        <ellipse cx="12" cy="12" rx="10" ry="4.2" />
        <ellipse cx="12" cy="12" rx="10" ry="4.2" transform="rotate(60 12 12)" />
        <ellipse cx="12" cy="12" rx="10" ry="4.2" transform="rotate(120 12 12)" />
      </svg>
      {compact ? 'PQ' : 'Quantum-Safe'}
    </span>
  );
}

// ---------- Section headers ----------
export function SectionHeader({ icon: Icon, title, desc, action }: {
  icon: React.ComponentType<{ className?: string }>; title: string; desc?: string; action?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 mb-4">
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-primary/10 ring-1 ring-primary/25 p-2.5 text-primary">
          <Icon className="size-5" />
        </div>
        <div>
          <h2 className="text-lg font-bold">{title}</h2>
          {desc && <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground text-sm">
      <Loader2 className="size-4 animate-spin" />
      {label ?? 'Loading…'}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, desc }: {
  icon: React.ComponentType<{ className?: string }>; title: string; desc?: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <div className="rounded-2xl bg-muted p-4 text-muted-foreground/60 mb-3">
        <Icon className="size-7" />
      </div>
      <div className="text-sm font-semibold">{title}</div>
      {desc && <div className="text-xs text-muted-foreground mt-1 max-w-xs">{desc}</div>}
    </div>
  );
}
